<script lang="ts" setup>
	import type { ModalOption } from '~/components/Dialogs/options.vue'
	import StringMethods from '~/utils/character'

	const props = withDefaults(
		defineProps<{
			list?: NotificationTodo[]
			noHeader?: boolean
			fetchTodo?: boolean
			limit?: number
		}>(),
		{
			limit: 4
		}
	)
	defineEmits<{
		(arg: 'get-item', param: TodoContext): void
	}>()
	const { todoRecords: todoLists } = reactor(useStore())
	const { productCategoryType } = useUtils()
	const { toCapitalize } = StringMethods()
	const isCustodianOptionOpen = shallowRef(false)
	const { toggle } = useNotificationActivities()
	const todoRecords = computed(() => props.list || todoLists.value || [])
	const highlightedTodo = shallowRef<{
		category: string
		planName: string
		purchaseId: string
		categoryName?: string
	} | null>({
		category: '',
		planName: '',
		purchaseId: ''
	})
	const categoryType = computed(() =>
		productCategoryType(highlightedTodo.value?.category, highlightedTodo.value?.planName)
	)

	const dialogOptions = computed<{
		title: string
		label: string
		data: ModalOption[]
	}>(() => {
		const customerType = categoryType.value?.isAuto
			? 'Policy Holders'
			: categoryType.value.isHealth || categoryType.value?.isTravel
				? 'Staff'
				: highlightedTodo.value?.category
		const title = `Upload ${customerType} to plan`
		const dialogIcon = {
			excel: {
				dir: 'documents',
				name: 'excel-sm',
				wrapperClass: 'bg-success-50 border-success-100'
			},
			person: {
				dir: 'fill',
				name: 'person-remove',
				wrapperClass: 'bg-success-50 border-success-100'
			},
			document: {
				dir: 'documents',
				name: 'export',
				wrapperClass: 'bg-success-50 border-success-100'
			}
		}

		return {
			title,
			label: `Select a method to add ${customerType} to your plan`,
			data: [
				{
					desc: `Upload list of ${customerType} to add via an excel sheet`,
					key: 'batch',
					name: 'Batch Method',
					icon: dialogIcon.excel
				},
				{
					desc: `Add ${customerType} via manual method `,
					key: 'manual',
					name: 'Manual Method',
					icon:
						categoryType.value.isHealth || categoryType.value?.isTravel
							? dialogIcon.person
							: dialogIcon.document
				}
			]
		}
	})
	const TodoProperties: Record<string, Omit<TodosRecord[string], 'callback'>> = {
		activation: {
			bg: 'bg-warning-50',
			textColor: 'text-warning-600',
			priority: 'quite',
			icon: {
				dir: 'fill',
				name: 'people'
			}
		},
		adding: {
			bg: 'bg-error-50',
			textColor: 'text-error-600',
			priority: 'high',
			icon: {
				name: 'alarm',
				dir: 'media',
				pathClass: 'fill-error-500'
			}
		}
	}
	const todos = computed(() => {
		const records = todoRecords.value.map(todo => {
			let properties: unknown = { ...todo }
			if (todo?.title?.includes('activation')) {
				properties = {
					...properties,
					label: todo?.text,
					...TodoProperties.activation,
					callback(arg: unknown) {
						const response = arg as NotificationTodo
						if (response.id) {
							navigateTo({
								path: `/manage-custodian/${response.purchase_id}/activate`
							})
						}
					}
				}
			} else if (todo?.title?.includes('add')) {
				properties = {
					...properties,

					...TodoProperties.adding,
					callback: (arg: unknown) => {
						const response = arg as NotificationTodo
						if (response.meta.id) {
							if (response.meta?.product?.name) {
								const id = response?.purchase_id || response?.meta?.purchase_id

								const planName = response.meta?.product?.name.toLowerCase().split(' ')[0]
								// Get the category name of the plan in order to route the user on which page to open
								const tabType =
									response.meta.product.productCategory?.name?.toLowerCase() === 'other'
										? planName
										: response.meta.product.productCategory.name
								highlightedTodo.value = {
									planName: response.meta.product.name,
									category: response.meta?.product?.productCategory?.name || '',
									purchaseId: id as string,
									categoryName: tabType
								}
								isCustodianOptionOpen.value = true
							}
						}
					}
				}
			}
			return properties
		}) as (TodosRecord[string] & NotificationTodo)[]
		return records.splice(0, props.limit)
	})

	const processTodo = (arg: ModalOption) => {
		const planN = highlightedTodo.value?.planName?.split(' ')[0]
		const planId = highlightedTodo.value?.purchaseId || ''
		const planCategory = highlightedTodo.value?.categoryName?.toLowerCase()

		let path = ''
		path = `/manage-custodian/${planId || ''}/add/new`

		if (planN && planCategory) {
			isCustodianOptionOpen.value = false
			navigateTo({
				path,
				query: {
					method: arg.key as string
				}
			})
			return null
		}
		useNotification().alert({
			status: 400,
			message: 'Something went wrong, please try again later.'
		})
	}
</script>

<template>
	<ul v-if="todoRecords.length" class="space-y-5">
		<slot v-if="!noHeader" name="header">
			<li class="flex flex-wrap items-center justify-between gap-x-2">
				<h3 class="text-sm font-medium text-blue-950 sm:text-lg">Key To-Dos</h3>
				<button
					v-if="Number(todoRecords?.length) > 4"
					type="button"
					aria-label="view all todos"
					class="text-sm tracking-wide text-blue-950 sm:text-base"
					@click="toggle"
				>
					View all
				</button>
			</li>
		</slot>

		<li
			v-for="(item, index) in todos"
			:key="index"
			class="flex-align-center light-shadow mb-3 h-[64px] w-full cursor-pointer justify-between rounded-md bg-white px-3 transition sm:px-5 hover:sm:-translate-x-1"
			@click="item?.callback(item)"
		>
			<slot v-if="item.title" :id="index" :key="item.title" :content="item">
				<div class="flex-align-center">
					<div
						class="flex-items-center h-[30px] w-[30px] flex-none rounded-full sm:h-[40px] sm:w-[40px]"
						:class="item?.bg"
					>
						<lazy-tools-icon
							:dir="item?.icon.dir"
							:icon="item?.icon?.name"
							:path-class="item?.icon?.pathClass"
						/>
					</div>

					<span class="max-w-md text-xs font-medium text-gray-600 sm:text-base">
						{{ toCapitalize(String(item?.label || item.text || '')) }}
					</span>
				</div>

				<div class="flex-align-center">
					<div
						v-if="item?.priority"
						class="hidden w-full max-w-[199px] rounded px-6 py-2.5 text-xs font-medium sm:block sm:text-base"
						:class="[item?.bg, item?.textColor]"
					>
						{{ item?.priority === 'high' ? 'Highly Important 🔥' : 'Quite Important 🤲🏽' }}
					</div>
					<lazy-tools-icon dir="arrows" icon="caret-right" path-class="stroke-primary-2 " />
				</div>
			</slot>
		</li>
		<li>
			<dialogs-options
				v-model:show="isCustodianOptionOpen"
				:category-options="dialogOptions.data"
				:title="dialogOptions.title"
				:paragraph="dialogOptions.label"
				@cancel="() => (highlightedTodo = null)"
				@proceed="processTodo"
			/>
		</li>
	</ul>
</template>

<style scoped>
	.flex-align-center {
		@apply flex items-center gap-x-3;
	}
	.flex-items-center {
		@apply flex items-center justify-center;
	}
</style>
